import styled from "@emotion/styled"

import Skeleton from "@mui/material/Skeleton"
import Stack from "@mui/material/Stack/Stack"
import { useQuery } from "@tanstack/react-query"

import BOTGDistributionChart from "./BOTGDistributionChart"
import BOTGPriceChart from "./BOTGPriceChart"

import { useAo } from "../../ao/ao"
import { BOTEGA_PROJECT_ADDRESS } from "../../settings"
import { NumberFont, TitleFont } from "../../theme"
import { formatNumberAuto, parseBigIntAsNumber } from "../../utils/format"

export type DataItem =
  | {
      DistributionTick: number
      YieldCycle: string
      Timestamp: number
      TotalMintedInTick: string
      TotalInflowInTick: string
      TriggerMintReportIds: string[]
    }
  | {
      Nonce: number
      PiReceived: string
      AoKept: string
      TokensDistributed: string
      TokenPriceInAo: string
      Timestamp: number
      AoReceived: string
      TriggerMintReportIds: string[]
    }

// Define interfaces for the response data
interface BOTGInfoResponse {
  "Data-Protocol": string
  Variant: string
  Type: string
  Reference: string
  "Delegation-Oracle": string
  "Token-Supply-To-Use": string
  "Decay-Factor": string
  "Withdrawn-Pi-Quantity": string
  "Token-Logo": string
  Owner: string
  "PI-Token-Process": string
  Socials: string
  FLP_FACTORY: string
  "Accumulated-Quantity": string
  "Withdrawn-Quantity": string
  "Exchanged-For-Pi-Quantity": string
  "Token-Ticker": string
  "Yield-Cycle": string
  "Distributed-Quantity": string
  "AO-Token": string
  "Are-General-Withdrawals-Enabled": string
  "Token-Disclaimer": string
  Action: string
  Status: string
  Deployer: string
  Treasury: string
  "Starts-At-Timestamp": string
  "PI-Process": string
  "Token-Process": string
  "Distribution-Tick": string
  "Accumulated-Pi-Quantity": string
  "Token-Denomination": string
  "Total-Token-Supply-At-Creation": string
  "Token-Name": string
}

// Parse specific fields for more usable formats
interface ParsedBOTGInfo {
  dataProtocol: string
  tokenTicker: string
  tokenName: string
  tokenDenomination: number
  distributionTick: number
  yieldCycle: string
  distributedQuantity: string
  accumulatedQuantity: string
  startsAtTimestamp: number
  status: string
  socials: {
    WebsiteURL: string
    TelegramHandle: string
    TwitterHandle: string
  }
  withdrawalsEnabled: boolean
  tokenLogo: string
  decayFactor: number
  raw: BOTGInfoResponse
}

export const BOTGStatsPanel = () => {
  const ao = useAo()

  const { data: flpInfo, isLoading } = useQuery<ParsedBOTGInfo>({
    queryKey: ["flpInfo"],
    queryFn: async () => {
      const result = await ao.dryrun({
        process: BOTEGA_PROJECT_ADDRESS,
        tags: [{ name: "Action", value: "Info" }],
      })

      if (result.Messages.length === 0) {
        throw new Error("No messages found")
      }

      const message = result.Messages[0]

      const rawTags = message.Tags.reduce(
        (obj: Partial<BOTGInfoResponse>, tag: { name: string; value: string }) => {
          obj[tag.name as keyof BOTGInfoResponse] = tag.value
          return obj
        },
        {} as BOTGInfoResponse,
      )

      // Parse and transform important fields
      return {
        dataProtocol: rawTags["Data-Protocol"],
        tokenTicker: rawTags["Token-Ticker"],
        tokenName: rawTags["Token-Name"],
        tokenDenomination: parseInt(rawTags["Token-Denomination"], 10),
        distributionTick: parseInt(rawTags["Distribution-Tick"], 10),
        yieldCycle: rawTags["Yield-Cycle"],
        distributedQuantity: rawTags["Distributed-Quantity"],
        accumulatedQuantity: rawTags["Accumulated-Quantity"],
        startsAtTimestamp: parseInt(rawTags["Starts-At-Timestamp"], 10),
        status: rawTags["Status"],
        socials: JSON.parse(rawTags["Socials"]),
        withdrawalsEnabled: rawTags["Are-General-Withdrawals-Enabled"] === "true",
        tokenLogo: rawTags["Token-Logo"],
        decayFactor: parseFloat(rawTags["Decay-Factor"]),
        raw: rawTags,
      }
    },
    refetchInterval: 10000,
  })

  // Query to fetch distribution history data
  const { data, isLoading: isHistoryLoading } = useQuery<DataItem[]>({
    queryKey: ["distributionHistory"],
    queryFn: async () => {
      const result = await ao.dryrun({
        process: BOTEGA_PROJECT_ADDRESS,
        tags: [{ name: "Action", value: "Get-Yield-Tick-History" }],
      })

      if (result.Messages.length === 0) {
        throw new Error("No distribution history data found")
      }

      // Parse the data from the result
      try {
        // Data is in the message Data field as JSON array
        return JSON.parse(result.Messages[0].Data)
      } catch (error) {
        console.error("Error parsing distribution history data:", error)
        return []
      }
    },
    refetchInterval: 10000,
  })

  const lastDistribution = !data
    ? {
        amount: 0,
        date: "Loading...",
      }
    : {
        amount: (() => {
          const lastItem = data[data.length - 1]
          if ("TotalMintedInTick" in lastItem) {
            return Number(parseBigIntAsNumber(BigInt(lastItem.TotalMintedInTick), 18))
          } else {
            return Number(parseBigIntAsNumber(BigInt(lastItem.TokensDistributed || "0"), 18))
          }
        })(),
        date: new Date(data[data.length - 1].Timestamp).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      }

  // Calculate next distribution
  const nextDistribution = {
    amount: 0,
    date: "Loading...",
  }

  if (flpInfo) {
    // Extract values needed for calculation
    const tokenSupplyToUse = flpInfo.raw["Token-Supply-To-Use"] || "0"
    const distributedQuantity = flpInfo.distributedQuantity || "0"
    const decayFactor = flpInfo.decayFactor || 0

    // Calculate remaining quantity and next distribution
    try {
      // Convert to big numbers and perform calculation
      const remainingQuantity = BigInt(tokenSupplyToUse) - BigInt(distributedQuantity)
      const remainingQuantityAsNumber = Number(
        parseBigIntAsNumber(remainingQuantity.toString(), flpInfo.tokenDenomination),
      )
      nextDistribution.amount = remainingQuantityAsNumber * (1 - decayFactor)

      console.log(flpInfo)

      // Calculate next distribution date
      const yieldCycle = 86400 // Default to 1 day if missing

      console.log("Yield Cycle:", yieldCycle)
      const nextDate = new Date()
      nextDate.setSeconds(nextDate.getSeconds() + yieldCycle)

      console.log("Next Date:", nextDate)

      nextDistribution.date = nextDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })

      console.log("Next Distribution Amount:", nextDistribution.amount)
    } catch (error) {
      console.error("Error calculating next distribution:", error)
    }
  }

  // Generate predicted distribution data for the chart
  const generatePredictedDistributions = (): DataItem[] => {
    if (!flpInfo || !data || data.length === 0) return []

    const predictions: DataItem[] = []

    // Extract values needed for calculation
    const tokenSupplyToUse = BigInt(flpInfo.raw["Token-Supply-To-Use"] || "0")
    const distributedQuantity = BigInt(flpInfo.distributedQuantity || "0")
    const decayFactor = flpInfo.decayFactor || 0
    const yieldCycle = parseInt(flpInfo.yieldCycle, 10) || 86400 // Default to 1 day if missing
    const tokenDenomination = flpInfo.tokenDenomination || 18

    // Calculate remaining quantity initially
    let remainingQuantity = tokenSupplyToUse - distributedQuantity
    let remainingQuantityAsNumber = Number(
      parseBigIntAsNumber(remainingQuantity.toString(), tokenDenomination),
    )

    // Start from the last distribution tick
    const lastItem = data[data.length - 1]
    const lastDistributionTick = "DistributionTick" in lastItem ? lastItem.DistributionTick : 0
    const lastTimestamp = lastItem.Timestamp

    for (let i = 0; i < 3; i++) {
      // Calculate next timestamp based on yield cycle
      const nextTimestamp = lastTimestamp + (i + 1) * yieldCycle * 1000 // Convert seconds to milliseconds

      // Calculate distribution amount with decay
      const distributionAmount = remainingQuantityAsNumber * (1 - decayFactor)

      // Convert back to bigint string format for consistency with API data
      const distributionAmountBigInt = BigInt(
        Math.floor(distributionAmount * 10 ** tokenDenomination),
      )

      // Get the latest inflow from historical data
      const lastItem = data[data.length - 1]
      const latestInflow =
        "TotalInflowInTick" in lastItem
          ? lastItem.TotalInflowInTick
          : "AoReceived" in lastItem
            ? lastItem.AoReceived
            : "0"

      predictions.push({
        DistributionTick: lastDistributionTick + i + 1,
        YieldCycle: flpInfo.yieldCycle,
        Timestamp: nextTimestamp,
        TotalMintedInTick: distributionAmountBigInt.toString(),
        TotalInflowInTick: latestInflow, // Use the latest known inflow
        TriggerMintReportIds: [], // Empty for predictions
      })

      // Update remaining quantity for next cycle
      remainingQuantityAsNumber -= distributionAmount
    }

    return predictions
  }

  // Get predicted distributions
  const predictedDistributions =
    !isLoading && !isHistoryLoading && data ? generatePredictedDistributions() : []

  // Combine historical and predicted data for the chart
  const combinedChartData = data ? [...data, ...predictedDistributions] : []

  return (
    <StatsWrapper>
      <Stack spacing={2} width="100%">
        <TypographyH6>$BOTG Distribution Stats</TypographyH6>

        <GridContainer>
          <GridItem>
            <StatsCard>
              <StatCardContent>
                <TypographySubtitle>Last Distribution</TypographySubtitle>
                {isLoading ? (
                  <Skeleton width={120} height={40} />
                ) : (
                  <ValueContainer>
                    <ValueWrapper>
                      <TypographyH5>{formatNumberAuto(lastDistribution.amount)}</TypographyH5>
                      <ValueUnit>BOTG</ValueUnit>
                    </ValueWrapper>
                    <TypographyBody>{lastDistribution.date}</TypographyBody>
                  </ValueContainer>
                )}
              </StatCardContent>
            </StatsCard>
          </GridItem>

          <GridItem>
            <StatsCard className="highlight">
              <StatCardContent>
                <TypographySubtitle>Upcoming Distribution</TypographySubtitle>
                {isLoading ? (
                  <Skeleton width={120} height={40} />
                ) : (
                  <ValueContainer>
                    <ValueWrapper>
                      <TypographyH5 className="accent">
                        {formatNumberAuto(nextDistribution.amount)}
                      </TypographyH5>
                      <ValueUnit className="accent">BOTG</ValueUnit>
                    </ValueWrapper>
                    <TypographyBody>Estimated {nextDistribution.date}</TypographyBody>
                  </ValueContainer>
                )}
              </StatCardContent>
            </StatsCard>
          </GridItem>

          <GridItem>
            <StatsCard>
              <StatCardContent>
                <TypographySubtitle>Next Distribution</TypographySubtitle>
                {isLoading ? (
                  <Skeleton width={120} height={40} />
                ) : (
                  <ValueContainer>
                    <ValueWrapper>
                      <TypographyH5>{nextDistribution.date}</TypographyH5>
                    </ValueWrapper>
                    <TypographyBody>{getCountdown(nextDistribution.date)}</TypographyBody>
                  </ValueContainer>
                )}
              </StatCardContent>
            </StatsCard>
          </GridItem>
        </GridContainer>

        <ChartContainer>
          {!data || isHistoryLoading ? (
            <Skeleton width="" height={220} />
          ) : (
            <BOTGPriceChart data={combinedChartData} isLoading={isHistoryLoading} />
          )}
        </ChartContainer>

        <ChartContainer>
          {!data || isHistoryLoading ? (
            <Skeleton width="" height={220} />
          ) : (
            <BOTGDistributionChart data={combinedChartData} isLoading={isHistoryLoading} />
          )}
        </ChartContainer>
      </Stack>
    </StatsWrapper>
  )
}
// Helper function to calculate countdown
const getCountdown = (dateString: string): string => {
  if (!dateString || dateString === "Loading...") return ""

  // Parse the month and day format (e.g., "April 5")
  const parts = dateString.split(" ")
  if (parts.length < 2) return ""

  const month = parts[0]
  const day = parseInt(parts[1], 10)

  const monthMap: { [key: string]: number } = {
    January: 0,
    February: 1,
    March: 2,
    April: 3,
    May: 4,
    June: 5,
    July: 6,
    August: 7,
    September: 8,
    October: 9,
    November: 10,
    December: 11,
  }

  if (!(month in monthMap)) return ""

  const now = new Date()
  const currentYear = now.getFullYear()

  // Create date with current year
  let targetDate = new Date(currentYear, monthMap[month], day)

  // If the date has already passed this year, use next year
  if (targetDate < now) {
    targetDate.setFullYear(currentYear + 1)
  }

  const diffTime = targetDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays > 1) return `In ${diffDays} days`
  if (diffDays === 1) return "Tomorrow"
  if (diffDays === 0) return "Today"
  return "Past date"
}
// Create typography components with different variants
const TypographyH6 = styled.div`
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-family: ${TitleFont};
`

const TypographySubtitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  font-family: ${TitleFont};
  margin-bottom: 0.5rem;
  color: var(--mui-palette-text-secondary);
`

const TypographyH5 = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  font-family: ${NumberFont};
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  margin: 0;

  &.accent {
    color: rgb(var(--theme-color));
  }
`

const TypographyBody = styled.div`
  font-size: 0.875rem;
  color: var(--mui-palette-text-secondary);
`

// Grid components
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.25rem;
  margin: 0.5rem 0 1.5rem 0;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const GridItem = styled.div`
  width: 100%;
`

const StatsWrapper = styled.div`
  padding: 1.5rem;
  width: 100%;
  border-radius: var(--mui-shape-borderRadius);
  margin-bottom: 1.5rem;
  background-color: var(--mui-palette-background-paper);
  display: flex;
  flex-direction: column;
`

const StatsCard = styled.div`
  padding: 1.5rem;
  height: 100%;
  border: 1px solid var(--mui-palette-divider);
  border-radius: var(--mui-shape-borderRadius);
  transition: all 0.3s ease;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

  [data-mui-color-scheme="dark"] & {
    background-color: var(--mui-palette-background-paper);
  }

  &.highlight {
    border-color: rgba(var(--theme-color), 0.3);
    background-color: rgba(var(--theme-color), 0.05);
    box-shadow: 0 4px 12px rgba(var(--theme-color), 0.08);
  }

  & .accent {
    color: rgb(var(--theme-color));
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);

    &.highlight {
      box-shadow: 0 6px 16px rgba(var(--theme-color), 0.12);
    }
  }
`

const ValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const ValueWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
`

const ValueUnit = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--mui-palette-text-secondary);

  &.accent {
    color: rgb(var(--theme-color));
  }
`

const StatCardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const ChartContainer = styled.div`
  padding: 1.25rem;
  border-radius: var(--mui-shape-borderRadius);
  background-color: #fff;
  min-height: 300px;

  [data-mui-color-scheme="dark"] & {
    background-color: var(--mui-palette-background-paper);
  }
`

export default BOTGStatsPanel
